import { canonicalizeCep } from '@/shared/format/cep';
import { canonicalizeCnpj } from '@/shared/format/cnpj';
import { canonicalizeCpf } from '@/shared/format/cpf';
import { nationalNumberBr } from '@/shared/format/phone';

import { renderTemplate, templateCapacity, type MaskModel } from './caret-mask';

export type MaskKind = 'cpf' | 'cnpj' | 'cep' | 'phone';

interface MaskSpec {
  readonly template: (significant: string) => string;
  readonly accepts: (character: string, index: number) => boolean;
  readonly capacity: number;
  readonly inputMode: 'numeric' | 'text';
  readonly placeholder: string;
  /** Lê um colar completo (com máscara, E.164…) para caracteres significativos, ou `null`. */
  readonly readPaste: (pasted: string) => string | null;
}

const DIGIT = /^[0-9]$/;
const ALNUM = /^[A-Z0-9]$/;
const PHONE_LANDLINE = '(##) ####-####';
const PHONE_MOBILE = '(##) #####-####';

const SPECS: Readonly<Record<MaskKind, MaskSpec>> = {
  cpf: {
    template: () => '###.###.###-##',
    accepts: (character) => DIGIT.test(character),
    capacity: 11,
    inputMode: 'numeric',
    placeholder: '000.000.000-00',
    readPaste: canonicalizeCpf,
  },
  cnpj: {
    // 12 alfanuméricos + 2 dígitos verificadores numéricos (IN RFB nº 2.229/2024).
    template: () => 'AA.AAA.AAA/AAAA-DD'.replace(/[AD]/g, '#'),
    accepts: (character, index) => (index < 12 ? ALNUM.test(character) : DIGIT.test(character)),
    capacity: 14,
    inputMode: 'text',
    placeholder: '00.AAA.000/0001-00',
    readPaste: canonicalizeCnpj,
  },
  cep: {
    template: () => '#####-###',
    accepts: (character) => DIGIT.test(character),
    capacity: 8,
    inputMode: 'numeric',
    placeholder: '00000-000',
    readPaste: canonicalizeCep,
  },
  phone: {
    template: (significant) => (significant.length > 10 ? PHONE_MOBILE : PHONE_LANDLINE),
    accepts: (character) => DIGIT.test(character),
    capacity: templateCapacity(PHONE_MOBILE),
    inputMode: 'numeric',
    placeholder: '(00) 00000-0000',
    // E.164 colado (`+5511987654321`) vira DDD + assinante, não os 11 primeiros dígitos.
    readPaste: nationalNumberBr,
  },
};

export function maskSpec(kind: MaskKind): MaskSpec {
  return SPECS[kind];
}

/** Modelo de caracteres para uma vaga: aceita o que o `accepts` deixa, em maiúsculas, até a capacidade. */
export function slotModel(
  template: (significant: string) => string,
  accepts: (character: string, index: number) => boolean,
  capacity: number,
): MaskModel {
  return {
    significant: (text) => {
      let significant = '';
      for (const character of text) {
        if (significant.length >= capacity) break;
        // Maiúscula antes do teste: `12abc…` e `12ABC…` são o mesmo CNPJ e só o segundo viaja.
        const candidate = character.toUpperCase();
        if (accepts(candidate, significant.length)) significant += candidate;
      }
      return significant;
    },
    render: (significant) => renderTemplate(template(significant), significant),
    anchor: 'left',
  };
}

export function maskModel(kind: MaskKind): MaskModel {
  const spec = SPECS[kind];
  return slotModel(spec.template, spec.accepts, spec.capacity);
}
